import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { useAuth } from "@/hooks/useAuth";

interface ContractEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: string;
  templateType: string;
  onSaved: () => void;
}

export const ContractEditDialog = ({
  open,
  onOpenChange,
  contractId,
  templateType,
  onSaved,
}: ContractEditDialogProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [savingSignature, setSavingSignature] = useState(false);

  useEffect(() => {
    if (open && contractId) {
      fetchContract();
    }
  }, [open, contractId]);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("id", contractId)
        .single();

      if (error) throw error;
      setFormData(data || {});
    } catch (error) {
      console.error("Error fetching contract:", error);
      toast.error("Fehler beim Laden des Dokuments");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("contract_templates")
        .update({
          ...formData,
          updated_at: new Date().toISOString(),
        })
        .eq("id", contractId);

      if (error) throw error;

      toast.success("Dokument wurde gespeichert");
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const renderEmploymentContractFields = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.person_name || ""}
            onChange={(e) => updateField("person_name", e.target.value)}
          />
        </div>
        <div>
          <Label>Position</Label>
          <Input
            value={formData.position || ""}
            onChange={(e) => updateField("position", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Abteilung</Label>
          <Input
            value={formData.department || ""}
            onChange={(e) => updateField("department", e.target.value)}
          />
        </div>
        <div>
          <Label>Startdatum</Label>
          <Input
            type="date"
            value={formData.start_date || ""}
            onChange={(e) => updateField("start_date", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Bruttogehalt</Label>
          <Input
            type="number"
            value={formData.base_salary || ""}
            onChange={(e) => updateField("base_salary", parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label>Urlaubstage</Label>
          <Input
            type="number"
            value={formData.vacation_days || ""}
            onChange={(e) => updateField("vacation_days", parseInt(e.target.value) || 0)}
          />
        </div>
      </div>
      <div>
        <Label>Wochenstunden</Label>
        <Input
          type="number"
          value={formData.weekly_hours || ""}
          onChange={(e) => updateField("weekly_hours", parseFloat(e.target.value) || 0)}
        />
      </div>
      <div>
        <Label>Zusätzliche Vereinbarungen</Label>
        <Textarea
          value={formData.custom_text || ""}
          onChange={(e) => updateField("custom_text", e.target.value)}
          rows={4}
        />
      </div>
    </div>
  );

  const renderContractAmendmentFields = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.person_name || ""}
            onChange={(e) => updateField("person_name", e.target.value)}
          />
        </div>
        <div>
          <Label>Gültig ab</Label>
          <Input
            type="date"
            value={formData.effective_date || ""}
            onChange={(e) => updateField("effective_date", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Alte Position</Label>
          <Input
            value={formData.old_position || ""}
            onChange={(e) => updateField("old_position", e.target.value)}
          />
        </div>
        <div>
          <Label>Neue Position</Label>
          <Input
            value={formData.new_position || ""}
            onChange={(e) => updateField("new_position", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Altes Gehalt</Label>
          <Input
            type="number"
            value={formData.old_salary || ""}
            onChange={(e) => updateField("old_salary", parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label>Neues Gehalt</Label>
          <Input
            type="number"
            value={formData.new_salary || ""}
            onChange={(e) => updateField("new_salary", parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>
      <div>
        <Label>Grund der Änderung</Label>
        <Textarea
          value={formData.amendment_reason || ""}
          onChange={(e) => updateField("amendment_reason", e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label>Zusätzliche Änderungen</Label>
        <Textarea
          value={formData.custom_text || ""}
          onChange={(e) => updateField("custom_text", e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );

  const renderCertificateFields = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.person_name || ""}
            onChange={(e) => updateField("person_name", e.target.value)}
          />
        </div>
        <div>
          <Label>Ausstellungsdatum</Label>
          <Input
            type="date"
            value={formData.issue_date || ""}
            onChange={(e) => updateField("issue_date", e.target.value)}
          />
        </div>
      </div>
      <div>
        <Label>Zertifikatstitel</Label>
        <Input
          value={formData.certificate_title || ""}
          onChange={(e) => updateField("certificate_title", e.target.value)}
        />
      </div>
      <div>
        <Label>Zertifikatsinhalt</Label>
        <Textarea
          value={formData.certificate_content || ""}
          onChange={(e) => updateField("certificate_content", e.target.value)}
          rows={6}
        />
      </div>
    </div>
  );

  const renderAnniversaryFields = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.person_name || ""}
            onChange={(e) => updateField("person_name", e.target.value)}
          />
        </div>
        <div>
          <Label>Position</Label>
          <Input
            value={formData.position || ""}
            onChange={(e) => updateField("position", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Dienstjahre</Label>
          <Input
            type="number"
            value={formData.years_of_service || ""}
            onChange={(e) => updateField("years_of_service", parseInt(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label>Jubiläumsdatum</Label>
          <Input
            type="date"
            value={formData.anniversary_date || ""}
            onChange={(e) => updateField("anniversary_date", e.target.value)}
          />
        </div>
      </div>
      <div>
        <Label>Persönlicher Text</Label>
        <Textarea
          value={formData.custom_text || ""}
          onChange={(e) => updateField("custom_text", e.target.value)}
          rows={4}
        />
      </div>
    </div>
  );

  const renderWarningFields = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.person_name || ""}
            onChange={(e) => updateField("person_name", e.target.value)}
          />
        </div>
        <div>
          <Label>Datum der Abmahnung</Label>
          <Input
            type="date"
            value={formData.warning_date || ""}
            onChange={(e) => updateField("warning_date", e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Position</Label>
          <Input
            value={formData.position || ""}
            onChange={(e) => updateField("position", e.target.value)}
          />
        </div>
        <div>
          <Label>Abmahnung Nr.</Label>
          <Input
            type="number"
            value={formData.warning_number || ""}
            onChange={(e) => updateField("warning_number", parseInt(e.target.value) || 1)}
          />
        </div>
      </div>
      <div>
        <Label>Grund der Abmahnung</Label>
        <Input
          value={formData.warning_reason || ""}
          onChange={(e) => updateField("warning_reason", e.target.value)}
        />
      </div>
      <div>
        <Label>Detaillierte Beschreibung</Label>
        <Textarea
          value={formData.warning_details || ""}
          onChange={(e) => updateField("warning_details", e.target.value)}
          rows={4}
        />
      </div>
      <div>
        <Label>Konsequenzen bei Wiederholung</Label>
        <Textarea
          value={formData.warning_consequences || ""}
          onChange={(e) => updateField("warning_consequences", e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );

  const renderFields = () => {
    switch (templateType) {
      case "employment_contract":
        return renderEmploymentContractFields();
      case "contract_amendment":
        return renderContractAmendmentFields();
      case "certificate":
        return renderCertificateFields();
      case "anniversary":
        return renderAnniversaryFields();
      case "warning":
        return renderWarningFields();
      default:
        return null;
    }
  };

  const supportsSignature = ["employment_contract", "contract_amendment", "warning", "vehicle_assignment"].includes(templateType);

  const handleSaveSignature = async (signatureDataUrl: string) => {
    if (!user) return;
    setSavingSignature(true);
    try {
      const { error } = await supabase
        .from("contract_templates")
        .update({
          signature_url: signatureDataUrl,
          signed_at: new Date().toISOString(),
          signed_by: user.id,
          status: "signed",
        })
        .eq("id", contractId);
      if (error) throw error;
      setFormData((prev) => ({
        ...prev,
        signature_url: signatureDataUrl,
        signed_at: new Date().toISOString(),
        signed_by: user.id,
        status: "signed",
      }));
      toast.success("Unterschrift wurde gespeichert");
      onSaved();
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    } finally {
      setSavingSignature(false);
    }
  };

  const handleDeleteSignature = async () => {
    setSavingSignature(true);
    try {
      const { error } = await supabase
        .from("contract_templates")
        .update({
          signature_url: null,
          signed_at: null,
          signed_by: null,
          status: "draft",
        })
        .eq("id", contractId);
      if (error) throw error;
      setFormData((prev) => ({
        ...prev,
        signature_url: null,
        signed_at: null,
        signed_by: null,
        status: "draft",
      }));
      toast.success("Unterschrift wurde entfernt");
      onSaved();
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Fehler beim Entfernen der Unterschrift");
    } finally {
      setSavingSignature(false);
    }
  };

  const getTitle = () => {
    switch (templateType) {
      case "employment_contract":
        return "Arbeitsvertrag bearbeiten";
      case "contract_amendment":
        return "Vertragsänderung bearbeiten";
      case "certificate":
        return "Zertifikat bearbeiten";
      case "anniversary":
        return "Jubiläumsurkunde bearbeiten";
      case "warning":
        return "Abmahnung bearbeiten";
      default:
        return "Dokument bearbeiten";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            Bearbeiten Sie die Dokumentdaten. Änderungen können nur gespeichert werden, solange das Dokument nicht festgesetzt ist.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {renderFields()}

            {supportsSignature && (
              <div className="pt-4 border-t">
                <SignatureCanvas
                  onSave={handleSaveSignature}
                  onDelete={handleDeleteSignature}
                  existingSignature={formData.signature_url || null}
                  signedAt={formData.signed_at || null}
                  signedByName={formData.person_name || null}
                  disabled={savingSignature}
                  isAdmin={true}
                  title="Unterschrift Mitarbeiter"
                  description="Unterschrift im Nachgang erfassen"
                />
              </div>
            )}


            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
